import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  MdHome, MdSchool, MdFamilyRestroom, MdAssignmentTurnedIn, MdEventAvailable,
  MdBarChart, MdCastForEducation, MdMenuBook, MdMeetingRoom, MdClass, MdSubject,
  MdSchedule, MdDirectionsBus, MdPayments, MdManageAccounts, MdNotifications, MdPalette, MdFactCheck, MdBackup, MdHub, MdLanguage,
  MdContactPhone, MdMonitorHeart, MdInfoOutline, MdHelpCenter, MdAutoAwesome, MdRocketLaunch, MdLocationCity, MdPhoneAndroid,
} from 'react-icons/md';
import { AuthProvider, useAuth } from './auth';
import Login from './Login';
import PhoneAppBanner from './PhoneAppBanner';
import { STUDENT_PORTAL_URL } from './studentPortal';
import { Button } from '../ui/PageShell';
import './login.css';
import Shell from './Shell';
import { NavIntentContext } from './navIntent';
import { IntroLayer, useIntroGate } from './intro/IntroGate';
import StudentDirectory from './pages/StudentDirectory';
import TeacherHome from './pages/TeacherHome';
import Monitor from './pages/Monitor';
import Students from './pages/Students';
import StudentImport from './pages/StudentImport';
import Families from './pages/Families';
import Teachers from './pages/Teachers';
import { Grades, Subjects, Batches, Sections } from './pages/Structure';
import Assignments from './pages/Assignments';
import Enrollments from './pages/Enrollments';
import Attendance from './pages/Attendance';
import Exams from './pages/Exams';
import Payments from './pages/Payments';
import Transportation from './pages/Transportation';
import Users from './pages/Users';
import AuditLog from './pages/AuditLog';
import Backups from './pages/Backups';
import Services from './pages/Services';
import Sites from './pages/Sites';
import Notifications from './pages/Notifications';
import Showcase from '../ui/Showcase';
import About from './pages/About';
import Help from './pages/Help';
import MishkahAI from './pages/MishkahAI';
import Roadmap from './pages/Roadmap';
import Rowad from './pages/Rowad';
import { CoachProvider } from './help/Coach';

// Every page, tagged with which roles may open it and which sidebar
// group it belongs to. The nav the Shell renders is this list filtered
// by the signed-in user's role — one source of truth for both.
//
// `featured` pages are the daily ones: they sit in «الصفحات الأساسية» at
// the top of the sidebar, and the first page a role can open is where it
// lands after login — شؤون الطلاب, the screen the office lives in.
const PAGES = [
  { id: 'teacher-home', label: 'صفوفي', icon: MdHome, group: 'العمل اليومي', featured: true, roles: ['teacher'], Component: TeacherHome },
  { id: 'student-directory', label: 'شؤون الطلاب', icon: MdContactPhone, group: 'العمل اليومي', featured: true, roles: ['admin', 'register', 'teacher'], Component: StudentDirectory },
  { id: 'monitor', label: 'لوحة المراقبة', icon: MdMonitorHeart, group: 'العمل اليومي', featured: true, roles: ['admin', 'register'], Component: Monitor },
  { id: 'notifications', label: 'الإشعارات', icon: MdNotifications, group: 'العمل اليومي', featured: true, roles: ['admin', 'register', 'teacher'], Component: Notifications },
  { id: 'mishkah', label: 'مشكاة الآلي', icon: MdAutoAwesome, group: 'العمل اليومي', featured: true, roles: ['admin', 'register', 'teacher'], Component: MishkahAI },

  { id: 'attendance', label: 'التفقّد', icon: MdEventAvailable, group: 'المدرسة', roles: ['admin', 'register', 'teacher'], Component: Attendance },
  { id: 'exams', label: 'الامتحانات', icon: MdBarChart, group: 'المدرسة', roles: ['admin', 'register', 'teacher'], Component: Exams },
  { id: 'payments', label: 'الأقساط', icon: MdPayments, group: 'المدرسة', roles: ['admin'], Component: Payments },
  { id: 'users', label: 'المستخدمون', icon: MdManageAccounts, group: 'المدرسة', roles: ['admin'], Component: Users },

  { id: 'students', label: 'الطلاب', icon: MdSchool, group: 'البيانات الأساسية', roles: ['admin', 'register'], Component: Students },
  { id: 'student-import', label: 'استيراد الطلاب', icon: MdAssignmentTurnedIn, group: 'البيانات الأساسية', roles: ['admin'], Component: StudentImport },
  { id: 'families', label: 'العائلات', icon: MdFamilyRestroom, group: 'البيانات الأساسية', roles: ['admin', 'register'], Component: Families },
  { id: 'enrollments', label: 'القيود', icon: MdAssignmentTurnedIn, group: 'البيانات الأساسية', roles: ['admin', 'register'], Component: Enrollments },
  { id: 'teachers', label: 'المعلمون', icon: MdCastForEducation, group: 'البيانات الأساسية', roles: ['admin'], Component: Teachers },
  { id: 'assignments', label: 'تدريس المواد', icon: MdMenuBook, group: 'البيانات الأساسية', roles: ['admin'], Component: Assignments },
  { id: 'sections', label: 'الشعب', icon: MdMeetingRoom, group: 'البيانات الأساسية', roles: ['admin'], Component: Sections },
  { id: 'grades', label: 'الصفوف', icon: MdClass, group: 'البيانات الأساسية', roles: ['admin'], Component: Grades },
  { id: 'subjects', label: 'المواد', icon: MdSubject, group: 'البيانات الأساسية', roles: ['admin'], Component: Subjects },
  { id: 'batches', label: 'الأفواج', icon: MdSchedule, group: 'البيانات الأساسية', roles: ['admin'], Component: Batches },
  { id: 'transportation', label: 'المواصلات', icon: MdDirectionsBus, group: 'البيانات الأساسية', roles: ['admin', 'register'], Component: Transportation },

  { id: 'backups', label: 'النسخ الاحتياطي', icon: MdBackup, group: 'إدارة النظام', roles: ['admin'], Component: Backups },
  { id: 'services', label: 'الخدمات المرتبطة', icon: MdHub, group: 'إدارة النظام', roles: ['admin'], Component: Services },
  { id: 'sites', label: 'المواقع والتطبيقات', icon: MdLanguage, group: 'إدارة النظام', roles: ['admin'], Component: Sites },
  { id: 'mobile-apps', label: 'تطبيقات الهواتف', icon: MdPhoneAndroid, group: 'إدارة النظام', roles: ['admin'], disabled: true },
  { id: 'audit-log', label: 'سجل العمليات', icon: MdFactCheck, group: 'إدارة النظام', roles: ['admin'], Component: AuditLog },

  { id: 'rowad', label: 'روّاد: حماس الطلاب', icon: MdLocationCity, group: 'بوابة مشكاة', roles: ['admin'], Component: Rowad },

  { id: 'showcase', label: 'معرض التصميم', icon: MdPalette, group: 'المساعدة والتطوير', roles: ['admin'], Component: Showcase },
  { id: 'help', label: 'دليل الاستخدام', icon: MdHelpCenter, group: 'المساعدة والتطوير', roles: ['admin', 'register', 'teacher'], Component: Help },
  { id: 'roadmap', label: 'التحديثات القادمة', icon: MdRocketLaunch, group: 'المساعدة والتطوير', roles: ['admin'], Component: Roadmap },
  { id: 'about', label: 'حول النظام', icon: MdInfoOutline, group: 'المساعدة والتطوير', roles: ['admin', 'register', 'teacher'], Component: About },
];

const ACTIVE_PAGE_KEY = 'mtz.activePage';

const pageHash = (id) => `#/${id}`;
const pageFromHash = () => decodeURIComponent((window.location.hash || '').replace(/^#\/?/, ''));

function Authenticated() {
  const { user, role } = useAuth();
  // «بوابة مشكاة» welcome: after the first login, and once per new version.
  const intro = useIntroGate(user);
  // Memoised: the navbar search builds its index from this list.
  const nav = useMemo(() => PAGES.filter((p) => p.roles.includes(role)), [role]);

  // The page in the address bar (#/monitor) wins, so a reload or a
  // bookmarked link opens that page; then the tab's last page.
  const [active, setActive] = useState(() => {
    const fromHash = pageFromHash();
    if (nav.some((p) => p.id === fromHash && !p.disabled)) return fromHash;
    const saved = sessionStorage.getItem(ACTIVE_PAGE_KEY);
    return nav.some((p) => p.id === saved && !p.disabled) ? saved : nav.find((p) => !p.disabled)?.id;
  });
  const activeRef = useRef(active);
  activeRef.current = active;

  const current = nav.find((p) => p.id === active && !p.disabled) || nav.find((p) => !p.disabled);
  const Page = current?.Component;

  // What the destination page should do on arrival ("open student
  // #782"), set by the navbar search — see navIntent.js.
  const [intent, setIntent] = useState(null);

  /** Switch page (and hand it its job) WITHOUT touching history. */
  const show = useCallback((id, payload) => {
    setActive(id);
    sessionStorage.setItem(ACTIVE_PAGE_KEY, id);
    setIntent(payload && Object.keys(payload).length ? { ...payload, page: id, nonce: Date.now() + Math.random() } : null);
  }, []);

  /**
   * Every in-app move is a browser history entry, so the browser's Back
   * and Forward walk the pages the user visited INSIDE the app
   * (لوحة المراقبة → آخر تفقّد → شؤون الطلاب, Back → لوحة المراقبة),
   * including what was open there: the intent ("student #782") is kept
   * in the entry and re-applied on the way back.
   */
  const navigate = useCallback((id, payload) => {
    if (!nav.some((p) => p.id === id && !p.disabled)) return;
    const hasIntent = payload && Object.keys(payload).length;
    if (id === activeRef.current && !hasIntent) return;
    window.history.pushState({ mtz: true, page: id, intent: hasIntent ? payload : null }, '', pageHash(id));
    show(id, payload);
  }, [show, nav]);

  useEffect(() => {
    // A guard entry sits UNDER the app's first page. Back from the first
    // page lands on it instead of leaving the site: the app steps
    // forward again and says how to leave; a second Back within a couple
    // of seconds really leaves. (A reload keeps both entries — the
    // browser restores history.state — so they are only created once.)
    const st = window.history.state;
    if (!st?.mtz) {
      window.history.replaceState({ mtz: true, guard: true }, '', pageHash(activeRef.current));
      window.history.pushState({ mtz: true, page: activeRef.current, intent: null }, '', pageHash(activeRef.current));
    } else if (st.guard) {
      window.history.pushState({ mtz: true, page: activeRef.current, intent: null }, '', pageHash(activeRef.current));
    }

    let lastGuardHit = 0;
    const onPop = (e) => {
      const s = e.state;
      if (!s?.mtz) return;
      if (s.guard) {
        if (Date.now() - lastGuardHit < 2500) { window.history.back(); return; }
        lastGuardHit = Date.now();
        window.history.pushState({ mtz: true, page: activeRef.current, intent: null }, '', pageHash(activeRef.current));
        toast.info('اضغط «رجوع» مرة أخرى للخروج من الموقع', { toastId: 'exit-hint', autoClose: 2500 });
        return;
      }
      show(s.page, s.intent);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [show]);

  const done = useCallback((nonce) => {
    setIntent((cur) => (cur && cur.nonce === nonce ? null : cur));
  }, []);
  const intentValue = useMemo(() => ({ intent, done, navigate }), [intent, done, navigate]);

  return (
    <NavIntentContext.Provider value={intentValue}>
      {/* «ورّيني»: the guides walk the user through the real pages. */}
      <CoachProvider user={user} role={role} nav={nav} page={current?.id}>
        <Shell nav={nav} active={current?.id} onNavigate={navigate} onReplayIntro={intro.replay} onOpenTour={intro.openTour}>
          {Page ? <Page /> : <p>لا توجد صفحات متاحة لهذا الحساب.</p>}
        </Shell>
      </CoachProvider>
      <IntroLayer gate={intro} user={user} />
    </NavIntentContext.Provider>
  );
}

// A student/parent session has no pages here — their app is the student
// portal. Say so and hand them the way out, instead of an empty shell.
function StudentAccountNotice() {
  const { user, logout } = useAuth();
  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">م</div>
          <div>
            <h1>هذا حساب طالب / ولي أمر</h1>
            <p>{user?.name}</p>
          </div>
        </div>
        <div className="login-form">
          <p className="login-hint">
            لوحة الإدارة مخصّصة للإدارة والمعلمين. يمكنك متابعة العلامات والحضور والأقساط من بوابة الطالب.
          </p>
          <Button type="button" onClick={() => { window.location.href = STUDENT_PORTAL_URL; }}>
            الانتقال إلى بوابة الطالب
          </Button>
          <Button type="button" variant="secondary" onClick={logout}>تسجيل الخروج</Button>
        </div>
      </div>
    </div>
  );
}

function Root() {
  const { user } = useAuth();
  if (!user) return <Login />;
  if (user.role === 'student') return <StudentAccountNotice />;
  return <><Authenticated /><PhoneAppBanner role={user.role} /></>;
}

export default function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}
